import React, { useState, useCallback, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Alert,
  ActivityIndicator,
  useWindowDimensions,
} from 'react-native';
import { getTwoCardMaxWidth, getCardMargin } from '../../config/configManager';
import { useProfileStore, useUserStore } from '../../contexts/StoreContext';
import type { ProfileConnectionDto } from '../../types/api/appProfile';
import type { AccountConnectionDto } from '../../types/api/service';
import type { ChildProfile } from '../../types/profile';

// ── Types ─────────────────────────────────────────────────────────────────────

type ConnectionType = 'Linked' | 'Secure Email';
type ConnectionPermission = 'Full Access' | 'Assessments & Reports' | 'Notifications Only';

const CONNECTION_TYPES: ConnectionType[] = ['Linked', 'Secure Email'];
const PERMISSIONS: ConnectionPermission[] = ['Full Access', 'Assessments & Reports', 'Notifications Only'];
const DATA_OPTIONS = [
  'Academic Research',
  'Public Health Agencies',
  'Help Me Grow',
  'Other Vetted Organizations',
] as const;

interface ConnectionsViewProps {
  apiService?: any;
  storage?: any;
}

const isValidEmail = (email: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

// ── Component ─────────────────────────────────────────────────────────────────

export const ConnectionsView: React.FC<ConnectionsViewProps> = ({ apiService, storage }) => {
  const { width } = useWindowDimensions();
  const isWide = width >= 768;

  // ── Profile store ─────────────────────────────────────────────────────────────
  const profileStore = useProfileStore();
  const { profiles: profilesRaw, updateProfile } = profileStore();
  const profiles: ChildProfile[] = (profilesRaw ?? []).filter((p: ChildProfile) => !p.isHidden);

  // Debug: log profile connection state on every render so we can trace when data arrives
  React.useEffect(() => {
    console.log('🔍 ConnectionsView: profiles in store:', profiles.length);
    profiles.forEach(p => {
      console.log(`  profile ${p.id} (${p.name}): connections=${p.connections?.length ?? 0}`,
        p.connections?.map(c => ({ id: c.ConnId, type: c.Type, name: c.DName })) ?? []);
    });
    if (profiles.length === 0) console.warn('🔍 ConnectionsView: no profiles in store — check profile sync');
  });

  // ── Per-profile add-form state ────────────────────────────────────────────────
  const [addFormProfileId, setAddFormProfileId] = useState<string | null>(null);
  const [newDisplayName, setNewDisplayName] = useState('');
  const [newType, setNewType] = useState<ConnectionType>('Linked');
  const [newPermissions, setNewPermissions] = useState<ConnectionPermission>('Assessments & Reports');
  const [newCode, setNewCode] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [codeError, setCodeError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [editConnId, setEditConnId] = useState<string | null>(null);
  const [isPrimary, setIsPrimary] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const userStore = useUserStore();

  // ── Data Connections state ───────────────────────────────────────────────────
  const [selectedData, setSelectedData] = useState<Set<string>>(
    new Set(DATA_OPTIONS),
  );
  const [isDataDirty, setIsDataDirty] = useState(false);
  const [isSavingData, setIsSavingData] = useState(false);

  useEffect(() => {
    const load = async () => {
      const gs = storage || (globalThis as any).__childNavStorage;
      if (!gs) return;
      try {
        const saved = await gs.getItem('data_sharing_selections');
        if (saved) {
          const parsed = JSON.parse(saved);
          if (Array.isArray(parsed)) setSelectedData(new Set(parsed));
        }
      } catch {}
    };
    load();
  }, [storage]);

  // ── API helpers ────────────────────────────────────────────────────────────────

  const permToApiCode = (perm: ConnectionPermission): string => {
    if (perm === 'Full Access') return '2';
    if (perm === 'Assessments & Reports') return '1';
    return '0'; // Notifications Only
  };

  const apiDtoToProfileConn = (dto: AccountConnectionDto): ProfileConnectionDto => ({
    ConnId: dto.PatConnId || Date.now().toString(),
    DName: dto.DName,
    Email: dto.Type === '0' ? dto.Link : '',
    Type: dto.Type === '1' ? 'Linked' : 'Secure Email',
    Permissions:
      dto.Permissions === '2' ? 'Full Access' :
      dto.Permissions === '1' ? 'Assessments & Reports' : 'Notifications Only',
    Primary: dto.Primary === '1' ? 'true' : '',
    Status: dto.Status,
  });

  // ── Handlers ──────────────────────────────────────────────────────────────────

  const closeForm = () => {
    setNewDisplayName('');
    setNewType('Linked');
    setNewPermissions('Assessments & Reports');
    setNewCode('');
    setNewEmail('');
    setCodeError('');
    setEmailError('');
    setIsPrimary(false);
    setShowDeleteConfirm(false);
    setEditConnId(null);
    setAddFormProfileId(null);
  };

  const openAddForm = (profileId: string) => {
    setNewDisplayName('');
    setNewType('Linked');
    setNewPermissions('Assessments & Reports');
    setNewCode('');
    setNewEmail('');
    setCodeError('');
    setEmailError('');
    setIsPrimary(false);
    setEditConnId(null);
    setAddFormProfileId(profileId);
  };

  const handleAddConnection = async (profile: ChildProfile) => {
    if (newType !== 'Linked' && !newDisplayName.trim()) {
      Alert.alert('Missing Field', 'Please enter a display name.');
      return;
    }
    if (newType === 'Linked') {
      if (!newCode.trim()) {
        setCodeError('Please enter the connection code from your provider.');
        return;
      }
    } else {
      if (!isValidEmail(newEmail)) {
        setEmailError('Please enter a valid email address.');
        return;
      }
    }
    if (!apiService || !storage) {
      Alert.alert('Sync Error', 'API service not available.');
      return;
    }
    setIsValidating(true);
    setCodeError('');
    setEmailError('');
    try {
      const userState = userStore && typeof (userStore as any).getState === 'function'
        ? (userStore as any).getState() : null;
      const accountId = userState?.session?.familyAccount?.familyId
        || userState?.session?.user?.userId
        || userState?.session?.user?.id
        || '0';
      const dto: AccountConnectionDto = {
        PatConnId: '',
        ProfileId: profile.id,
        Type: newType === 'Linked' ? '1' : '0',
        DName: newDisplayName.trim(),
        Link: newType === 'Linked' ? newCode.trim() : newEmail.trim(),
        Primary: isPrimary ? '1' : '0',
        Permissions: permToApiCode(newPermissions),
        resources: [],
        Status: '0',
      };
      void accountId; // included in auth token; DTO fields sufficient
      const request = { RequestType: 'account_put_patconn', Payload: JSON.stringify(dto) };
      const response = await apiService.processServiceRequestWithRefresh(request, 'sync', {
        userStore,
        storage,
      });
      if (!response?.success) {
        throw new Error(response?.message || 'Failed to add connection');
      }
      const updatedDto: AccountConnectionDto = JSON.parse(response.payload || '{}');
      if (!updatedDto.PatConnId) {
        throw new Error(
          newType === 'Linked'
            ? 'Provider code not found. Please verify the code and try again.'
            : response?.message || 'Connection could not be created. Please try again.'
        );
      }
      const newConn = apiDtoToProfileConn(updatedDto);
      const existing = profile.connections ?? [];
      const updated = isPrimary
        ? [...existing.map(c => ({ ...c, Primary: '' })), newConn]
        : [...existing, newConn];
      updateProfile(profile.id, { connections: updated });
      closeForm();
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      if (newType === 'Linked') setCodeError(msg);
      else Alert.alert('Connection Failed', msg);
    } finally {
      setIsValidating(false);
    }
  };

  const openEditForm = (profile: ChildProfile, conn: ProfileConnectionDto) => {
    setNewDisplayName(conn.DName);
    setNewType(conn.Type as ConnectionType);
    setNewPermissions(conn.Permissions as ConnectionPermission);
    setNewCode('');
    setNewEmail(conn.Email ?? '');
    setCodeError('');
    setEmailError('');
    setIsPrimary(conn.Primary === 'true');
    setEditConnId(conn.ConnId);
    setAddFormProfileId(profile.id);
  };

  const handleSaveConnection = async (profile: ChildProfile) => {
    if (newType !== 'Linked' && !newDisplayName.trim()) {
      Alert.alert('Missing Field', 'Please enter a display name.');
      return;
    }
    if (newType === 'Secure Email' && !isValidEmail(newEmail)) {
      setEmailError('Please enter a valid email address.');
      return;
    }
    if (!apiService || !storage) {
      Alert.alert('Sync Error', 'API service not available.');
      return;
    }
    setIsValidating(true);
    setEmailError('');
    try {
      const conn = (profile.connections ?? []).find(c => c.ConnId === editConnId);
      const dto: AccountConnectionDto = {
        PatConnId: conn?.ConnId ?? editConnId ?? '',
        ProfileId: profile.id,
        Type: newType === 'Linked' ? '1' : '0',
        DName: newDisplayName.trim(),
        Link: newType === 'Secure Email' ? newEmail.trim() : (newCode.trim() || ''),
        Primary: isPrimary ? '1' : '0',
        Permissions: permToApiCode(newPermissions),
        resources: [],
        Status: '1',
      };
      const request = { RequestType: 'account_put_patconn', Payload: JSON.stringify(dto) };
      const response = await apiService.processServiceRequestWithRefresh(request, 'sync', {
        userStore,
        storage,
      });
      if (!response?.success) {
        throw new Error(response?.message || 'Failed to save connection');
      }
      const updatedDto: AccountConnectionDto = JSON.parse(response.payload || '{}');
      const updatedConn = apiDtoToProfileConn(updatedDto);
      const updated = (profile.connections ?? []).map(c => {
        if (c.ConnId === editConnId) return updatedConn;
        return isPrimary ? { ...c, Primary: '' } : c;
      });
      updateProfile(profile.id, { connections: updated });
      closeForm();
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      Alert.alert('Save Failed', msg);
    } finally {
      setIsValidating(false);
    }
  };

  const confirmDeleteConnection = async (profile: ChildProfile) => {
    if (!apiService || !storage) {
      Alert.alert('Sync Error', 'API service not available.');
      return;
    }
    const conn = (profile.connections ?? []).find(c => c.ConnId === editConnId);
    setIsValidating(true);
    try {
      const dto: AccountConnectionDto = {
        PatConnId: conn?.ConnId ?? editConnId ?? '',
        ProfileId: profile.id,
        Type: '',
        DName: '',
        Link: '',
        Primary: '',
        Permissions: '',
        resources: [],
        Status: '-1',
      };
      const request = { RequestType: 'account_put_patconn', Payload: JSON.stringify(dto) };
      const response = await apiService.processServiceRequestWithRefresh(request, 'sync', {
        userStore,
        storage,
      });
      if (!response?.success) {
        throw new Error(response?.message || 'Failed to delete connection');
      }
      const updated = (profile.connections ?? []).filter(c => c.ConnId !== editConnId);
      updateProfile(profile.id, { connections: updated });
      closeForm();
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      Alert.alert('Delete Failed', msg);
    } finally {
      setIsValidating(false);
    }
  };

  const toggleDataOption = (option: string) => {
    setSelectedData(prev => {
      const next = new Set(prev);
      if (next.has(option)) next.delete(option);
      else next.add(option);
      return next;
    });
    setIsDataDirty(true);
  };

  const saveDataConnections = useCallback(async () => {
    if (!apiService || !storage) {
      Alert.alert('Sync Error', 'API service or storage not available.');
      return;
    }
    setIsSavingData(true);
    try {
      const dataSharingSettings = [
        { Name: 'data_share_academic_research',      Value: selectedData.has('Academic Research')         ? 'true' : 'false' },
        { Name: 'data_share_public_health_agencies', Value: selectedData.has('Public Health Agencies')     ? 'true' : 'false' },
        { Name: 'data_share_help_me_grow',           Value: selectedData.has('Help Me Grow')              ? 'true' : 'false' },
        { Name: 'data_share_other_vetted_orgs',      Value: selectedData.has('Other Vetted Organizations') ? 'true' : 'false' },
      ];
      const userState = userStore && typeof (userStore as any).getState === 'function'
        ? (userStore as any).getState()
        : null;
      const userId = userState?.session?.user?.userId
        || userState?.session?.user?.id
        || userState?.session?.familyAccount?.familyId
        || '0';
      const payload = { UserId: userId, settings: dataSharingSettings };
      const request = { RequestType: 'account_put_settings', Payload: JSON.stringify(payload) };
      const response = await apiService.processServiceRequestWithRefresh(request, 'sync', {
        userStore,
        storage,
      });
      if (!response?.success) {
        throw new Error(response?.message || 'Backend returned an error');
      }
      await storage.setItem('data_sharing_selections', JSON.stringify([...selectedData]));
      setIsDataDirty(false);
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      Alert.alert('Sync Failed', `Could not save data connections: ${msg}`);
    } finally {
      setIsSavingData(false);
    }
  }, [apiService, storage, userStore, selectedData]);

  // ── Render ─────────────────────────────────────────────────────────────────────

  const renderSection = (title: string, children: React.ReactNode, trailingAction?: React.ReactNode) => (
    <View style={styles.section}>
      <View style={styles.sectionTitleRow}>
        <Text style={styles.sectionTitle}>{title}</Text>
        {trailingAction}
      </View>
      {children}
    </View>
  );

  const renderProfileConnectionsContent = () => (
    <>
      <Text style={styles.cardDescription}>
        Provider, agency and services connections.
      </Text>
      {profiles.length === 0 && (
        <View style={styles.emptyState}>
          <Text style={styles.emptyText}>No child profiles available</Text>
        </View>
      )}
      {profiles.map(profile => renderProfileCard(profile))}
      <View style={styles.cardFooter}>
        <Text style={styles.cardFooterText}>⭐ Free upgrade to Premium Lifetime when you add a linked connection</Text>
      </View>
    </>
  );

  const renderConnectionForm = (profile: ChildProfile) => (
    <View style={styles.addForm}>
      {!editConnId && (
        <>
          <Text style={[styles.fieldLabel, { marginTop: 0 }]}>Connection Type</Text>
          <View style={styles.segmentRow}>
            {CONNECTION_TYPES.map(t => (
              <TouchableOpacity
                key={t}
                style={[styles.segmentBtn, newType === t && styles.segmentBtnActive]}
                onPress={() => { setNewType(t); setCodeError(''); setEmailError(''); }}
              >
                <Text style={[styles.segmentBtnText, newType === t && styles.segmentBtnTextActive]}>
                  {t}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </>
      )}

      {newType !== 'Linked' && (
        <>
          <Text style={styles.fieldLabel}>Display Name</Text>
          <TextInput
            style={styles.input}
            value={newDisplayName}
            onChangeText={setNewDisplayName}
            placeholder="e.g. Dr. Smith's Office"
            placeholderTextColor="#aaa"
          />
        </>
      )}

      {newType === 'Linked' ? (
        !editConnId ? (
          <>
            <Text style={styles.fieldLabel}>Provider Code</Text>
            <Text style={styles.fieldHint}>Enter the connection code from your provider.</Text>
            <TextInput
              style={[styles.input, codeError ? styles.inputError : null]}
              value={newCode}
              onChangeText={v => { setNewCode(v); setCodeError(''); }}
              placeholder="Connection code from provider"
              placeholderTextColor="#aaa"
              autoCapitalize="none"
              autoCorrect={false}
            />
            {!!codeError && <Text style={styles.errorText}>{codeError}</Text>}
          </>
        ) : null
      ) : (
        <>
          <Text style={styles.fieldLabel}>Email Address</Text>
          <TextInput
            style={[styles.input, emailError ? styles.inputError : null]}
            value={newEmail}
            onChangeText={v => { setNewEmail(v); setEmailError(''); }}
            placeholder="provider@example.com"
            placeholderTextColor="#aaa"
            keyboardType="email-address"
            autoCapitalize="none"
            autoCorrect={false}
          />
          {!!emailError && <Text style={styles.errorText}>{emailError}</Text>}
        </>
      )}

      <View style={styles.fieldLabelRow}>
        <Text style={[styles.fieldLabel, { marginTop: 0, marginBottom: 0 }]}>Permissions</Text>
        <TouchableOpacity style={styles.primaryInlineRow} onPress={() => setIsPrimary(!isPrimary)}>
          <View style={[styles.checkbox, isPrimary && styles.checkboxChecked]}>
            {isPrimary && <Text style={styles.checkmark}>✓</Text>}
          </View>
          <Text style={styles.primaryInlineLabel}>Make Primary</Text>
        </TouchableOpacity>
      </View>
      {PERMISSIONS.map(p => (
        <TouchableOpacity
          key={p}
          style={styles.radioRow}
          onPress={() => setNewPermissions(p)}
        >
          <View style={[styles.radioOuter, newPermissions === p && styles.radioOuterSelected]}>
            {newPermissions === p && <View style={styles.radioInner} />}
          </View>
          <Text style={styles.radioLabel}>{p}</Text>
        </TouchableOpacity>
      ))}

      <View style={styles.formButtons}>
        {editConnId ? (
          <>
            {showDeleteConfirm ? (
              <>
                <Text style={{ flex: 1, fontSize: 13, color: '#e74c3c', alignSelf: 'center' }}>Delete this connection?</Text>
                <TouchableOpacity style={styles.cancelBtn} onPress={() => setShowDeleteConfirm(false)}>
                  <Text style={styles.cancelBtnText}>No</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.deleteBtn} onPress={() => confirmDeleteConnection(profile)}>
                  <Text style={styles.deleteBtnText}>Yes, Delete</Text>
                </TouchableOpacity>
              </>
            ) : (
              <>
                <TouchableOpacity style={styles.deleteBtn} onPress={() => setShowDeleteConfirm(true)}>
                  <Text style={styles.deleteBtnText}>Delete</Text>
                </TouchableOpacity>
                <View style={{ flex: 1 }} />
                <TouchableOpacity style={styles.cancelBtn} onPress={closeForm}>
                  <Text style={styles.cancelBtnText}>Cancel</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.saveBtn, isValidating && styles.saveBtnDisabled]}
                  onPress={() => handleSaveConnection(profile)}
                  disabled={isValidating}
                >
                  <Text style={styles.saveBtnText}>
                    {isValidating ? 'Saving…' : 'Save'}
                  </Text>
                </TouchableOpacity>
              </>
            )}
          </>
        ) : (
          <>
            <TouchableOpacity style={styles.cancelBtn} onPress={closeForm}>
              <Text style={styles.cancelBtnText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.saveBtn, isValidating && styles.saveBtnDisabled]}
              onPress={() => handleAddConnection(profile)}
              disabled={isValidating}
            >
              <Text style={styles.saveBtnText}>
                {isValidating ? 'Saving…' : 'Add Connection'}
              </Text>
            </TouchableOpacity>
          </>
        )}
      </View>
    </View>
  );

  const renderProfileCard = (profile: ChildProfile) => {
    const conns = profile.connections ?? [];
    console.log(`🔍 ConnectionsView renderProfileCard: profile=${profile.id} (${profile.name}), connections=${conns.length}`);
    const isFormOpen = addFormProfileId === profile.id;
    return (
      <View key={profile.id} style={styles.profileSubCard}>

        {/* Profile header */}
        <View style={styles.profileHeader}>
          <Text style={styles.profileName}>{profile.name}</Text>
          {!isFormOpen && (
            <TouchableOpacity style={styles.inlineAddBtn} onPress={() => openAddForm(profile.id)}>
              <Text style={styles.inlineAddBtnText}>＋  Add Connection</Text>
            </TouchableOpacity>
          )}
        </View>

        {conns.length === 0 && !isFormOpen && (
          <View style={styles.emptyState}>
            <Text style={styles.emptyText}>No connections added yet</Text>
          </View>
        )}

        {conns.map((conn, connIdx) => {
          const isEditing = !!editConnId && editConnId === conn.ConnId && isFormOpen;
          const connKey = conn.ConnId || `${profile.id}_idx_${connIdx}`;
          return (
            <View key={connKey} style={[styles.connectionSubCard, isEditing && styles.connectionSubCardEditing]}>
              <View style={styles.connectionItem}>
                <View style={styles.connectionRow}>
                  <Text style={styles.connectionName}>{conn.DName}{conn.Primary === 'true' ? ' ★' : ''}</Text>
                  <View style={[
                    styles.typeBadge,
                    conn.Type === 'Linked' ? styles.typeBadgeLinked : styles.typeBadgeEmail,
                  ]}>
                    <Text style={styles.typeBadgeText}>{conn.Type}</Text>
                  </View>
                </View>
                <View style={styles.connectionFooter}>
                  <Text style={styles.connectionPermissions}>{conn.Permissions}</Text>
                  <TouchableOpacity onPress={isEditing ? closeForm : () => openEditForm(profile, conn)}>
                    <Text style={[styles.editText, isEditing && { color: '#7f8c8d' }]}>
                      {isEditing ? 'Cancel' : 'Edit'}
                    </Text>
                  </TouchableOpacity>
                </View>
                {!!conn.Email && <Text style={styles.connectionEmail}>{conn.Email}</Text>}
              </View>
              {isEditing && renderConnectionForm(profile)}
            </View>
          );
        })}

        {isFormOpen && editConnId === null && renderConnectionForm(profile)}
      </View>
    );
  };

  const renderDataConnectionsContent = () => (
    <>
      <Text style={styles.cardDescription}>
        Share my data, with identifying data removed, for research and public health management.
      </Text>
      {DATA_OPTIONS.map(option => {
        const checked = selectedData.has(option);
        return (
          <TouchableOpacity
            key={option}
            style={styles.checkRow}
            onPress={() => toggleDataOption(option)}
            activeOpacity={0.7}
          >
            <View style={[styles.checkbox, checked && styles.checkboxChecked]}>
              {checked && <Text style={styles.checkmark}>✓</Text>}
            </View>
            <Text style={styles.checkLabel}>{option}</Text>
          </TouchableOpacity>
        );
      })}
    </>
  );

  return (
    <View style={styles.backgroundContainer}>
      <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
        {/* Header */}
        <View style={[styles.header, { maxWidth: getTwoCardMaxWidth(), alignSelf: 'center', width: '100%' }]}>
          <Text style={styles.title}>🔗  Connections</Text>
          <Text style={styles.subtitle}>Manage your child's connections and data sharing preferences</Text>
        </View>

        {/* Cards */}
        <View style={{ maxWidth: getTwoCardMaxWidth(), alignSelf: 'center', width: '100%', paddingHorizontal: getCardMargin() }}>
          <View style={{ flexDirection: isWide ? 'row' : 'column', gap: 16, alignItems: isWide ? 'flex-start' : 'stretch' }}>

            {/* Card 1: Profile Connections */}
            <View style={{ flex: isWide ? 1 : undefined }}>
              {renderSection('🧒 Profile Connections', renderProfileConnectionsContent())}
            </View>

            {/* Card 2: Data Connections */}
            <View style={{ flex: isWide ? 1 : undefined }}>
              {renderSection('📊 Data Connections', renderDataConnectionsContent(),
                isDataDirty ? (
                  <TouchableOpacity
                    style={[styles.sectionSaveLink, isSavingData && styles.sectionSaveLinkDisabled]}
                    onPress={saveDataConnections}
                    disabled={isSavingData}
                  >
                    {isSavingData
                      ? <ActivityIndicator size="small" color="#3498db" />
                      : <Text style={styles.sectionSaveLinkText}>Save</Text>
                    }
                  </TouchableOpacity>
                ) : undefined
              )}
            </View>

          </View>
        </View>
      </ScrollView>
    </View>
  );
};

// ── Styles ────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  backgroundContainer: {
    flex: 1,
    backgroundColor: '#3b82f6',
  },
  container: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  contentContainer: {
    backgroundColor: '#3b82f6',
    paddingVertical: 16,
  },
  header: {
    backgroundColor: 'transparent',
    padding: 24,
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#ffffff',
    marginBottom: 8,
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 4,
  },
  subtitle: {
    fontSize: 16,
    color: '#e2e8f0',
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
  profileSubCard: {
    backgroundColor: '#f8f9ff',
    borderRadius: 8,
    marginHorizontal: 12,
    marginBottom: 10,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#dde1f0',
  },

  // ── Section card ─────────────────────────────────────────────────────────────
  section: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    paddingTop: 16,
    paddingBottom: 8,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    marginBottom: 16,
  },
  sectionTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    marginBottom: 4,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#2c3e50',
    flex: 1,
  },
  sectionSaveLink: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
  },
  sectionSaveLinkDisabled: {
    opacity: 0.5,
  },
  sectionSaveLinkText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#3498db',
  },
  cardFooter: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: '#f1f2f6',
    marginTop: 4,
  },
  cardFooterText: {
    fontSize: 13,
    color: '#7f8c8d',
    textAlign: 'center',
    lineHeight: 18,
  },
  cardDescription: {
    fontSize: 14,
    color: '#7f8c8d',
    paddingHorizontal: 20,
    marginBottom: 12,
    lineHeight: 20,
  },

  // ── Per-profile blocks ───────────────────────────────────────────────────
  profileBlock: {
    paddingTop: 12,
    paddingBottom: 4,
  },
  profileBlockDivider: {
    borderTopWidth: 1,
    borderTopColor: '#e2e8f0',
    marginTop: 4,
  },
  profileHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingTop: 10,
    marginBottom: 4,
  },
  profileName: {
    fontSize: 15,
    fontWeight: '700',
    color: '#1e3a5f',
  },
  connectionCount: {
    fontSize: 12,
    color: '#7f8c8d',
  },
  inlineAddBtn: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    backgroundColor: '#eff6ff',
    borderWidth: 1,
    borderColor: '#bfdbfe',
  },
  inlineAddBtnText: {
    fontSize: 13,
    color: '#3b82f6',
    fontWeight: '600',
  },
  connectionEmail: {
    fontSize: 12,
    color: '#7f8c8d',
    marginTop: 2,
  },

  // ── Connection list ───────────────────────────────────────────────────────────
  emptyState: {
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  emptyText: {
    fontSize: 14,
    color: '#7f8c8d',
    textAlign: 'center',
  },
  connectionSubCard: {
    backgroundColor: '#ffffff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    marginHorizontal: 12,
    marginBottom: 8,
    overflow: 'hidden',
  },
  connectionSubCardEditing: {
    borderColor: '#3b82f6',
    backgroundColor: '#eff6ff',
  },
  connectionItem: {
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  connectionItemEditing: {
    backgroundColor: '#eff6ff',
    borderLeftWidth: 3,
    borderLeftColor: '#3b82f6',
  },
  connectionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  connectionName: {
    fontSize: 15,
    fontWeight: '600',
    color: '#2c3e50',
    flex: 1,
    marginRight: 8,
  },
  typeBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
  },
  typeBadgeLinked: {
    backgroundColor: '#dbeafe',
  },
  typeBadgeEmail: {
    backgroundColor: '#dcfce7',
  },
  typeBadgeText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#374151',
  },
  connectionFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  connectionPermissions: {
    fontSize: 13,
    color: '#7f8c8d',
  },
  editText: {
    fontSize: 13,
    color: '#3b82f6',
    fontWeight: '500',
  },

  // ── Add connection form ───────────────────────────────────────────────────────
  addForm: {
    paddingHorizontal: 20,
    paddingTop: 12,
    paddingBottom: 8,
    borderTopWidth: 1,
    borderTopColor: '#f1f2f6',
  },
  fieldLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 12,
    marginBottom: 6,
  },
  primaryInlineRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  primaryInlineLabel: {
    fontSize: 12,
    color: '#374151',
    fontWeight: '500',
  },
  fieldLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 6,
    marginTop: 12,
  },
  fieldHint: {
    fontSize: 12,
    color: '#7f8c8d',
    marginBottom: 6,
    marginTop: -4,
  },
  input: {
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 14,
    color: '#2c3e50',
    backgroundColor: '#f9fafb',
  },
  inputError: {
    borderColor: '#e74c3c',
  },
  errorText: {
    fontSize: 12,
    color: '#e74c3c',
    marginTop: 4,
  },
  segmentRow: {
    flexDirection: 'row',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#d1d5db',
    overflow: 'hidden',
  },
  segmentBtn: {
    flex: 1,
    paddingVertical: 8,
    alignItems: 'center',
    backgroundColor: '#f9fafb',
  },
  segmentBtnActive: {
    backgroundColor: '#3b82f6',
  },
  segmentBtnText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
  },
  segmentBtnTextActive: {
    color: '#ffffff',
  },
  radioRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 7,
    gap: 10,
  },
  radioOuter: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: '#d1d5db',
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioOuterSelected: {
    borderColor: '#3b82f6',
  },
  radioInner: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#3b82f6',
  },
  radioLabel: {
    fontSize: 14,
    color: '#2c3e50',
  },
  formButtons: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 10,
    marginTop: 16,
    marginBottom: 4,
  },
  cancelBtn: {
    paddingHorizontal: 16,
    paddingVertical: 9,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#d1d5db',
    backgroundColor: '#f9fafb',
  },
  cancelBtnText: {
    fontSize: 14,
    color: '#374151',
    fontWeight: '500',
  },
  saveBtn: {
    paddingHorizontal: 16,
    paddingVertical: 9,
    borderRadius: 8,
    backgroundColor: '#3b82f6',
  },
  saveBtnDisabled: {
    backgroundColor: '#93c5fd',
  },
  saveBtnText: {
    fontSize: 14,
    color: '#ffffff',
    fontWeight: '600',
  },
  deleteBtn: {
    paddingHorizontal: 16,
    paddingVertical: 9,
    borderRadius: 8,
    backgroundColor: '#fee2e2',
    borderWidth: 1,
    borderColor: '#fca5a5',
  },
  deleteBtnText: {
    fontSize: 14,
    color: '#dc2626',
    fontWeight: '600',
  },
  addButton: {
    paddingHorizontal: 20,
    paddingVertical: 14,
    borderTopWidth: 1,
    borderTopColor: '#f1f2f6',
    alignItems: 'center',
  },
  addButtonText: {
    fontSize: 15,
    color: '#3b82f6',
    fontWeight: '600',
  },

  // ── Data Connections checkboxes ───────────────────────────────────────────────
  checkRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: '#f1f2f6',
    gap: 12,
  },
  checkbox: {
    width: 22,
    height: 22,
    borderRadius: 5,
    borderWidth: 2,
    borderColor: '#d1d5db',
    backgroundColor: '#f9fafb',
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxChecked: {
    backgroundColor: '#3b82f6',
    borderColor: '#3b82f6',
  },
  checkmark: {
    fontSize: 13,
    color: '#ffffff',
    fontWeight: 'bold',
    lineHeight: 16,
  },
  checkLabel: {
    fontSize: 15,
    color: '#2c3e50',
    flex: 1,
  },
});